import { COLUMN_CHART, LINE_CHART, SCATTER_CHART } from '../../../utils/dashboardConsts';
import { lineColors } from '../../../utils/charts';
import { getChartDataSettings } from '../../../components/partials/PivotChart/utils';
import { BREAKDOWN_DIMENSIONS } from './consts';

export const buildBaseChartOptions = ({
    chartType,
    series,
    legendEnabled,
    tooltipFormatter,
    xAxisTitle,
    xLabelRotation,
}) => ({
    title: { text: null },
    chart: {
        type: chartType,
        height: 250,
        animation: false,
        zoomType: 'xy',
        resetZoomButton: {
            theme: {
                fill: '#FFFFFF99',
                stroke: '#334155',
                style: {
                    color: '#334155',
                    fontSize: '0.8rem',
                },
                states: {
                    hover: {
                        fill: '#334155',
                        style: {
                            color: 'white',
                        },
                    },
                },
            },
            position: {
                align: 'right',
                verticalAlign: 'top',
                x: -10,
                y: 10,
            },
        },
    },
    xAxis: {
        title: xAxisTitle && { text: xAxisTitle },
        labels: {
            enabled: true,
            style: {
                fontSize: '11px',
            },
            rotation: xLabelRotation ?? 0,
        },
    },
    legend: {
        enabled: legendEnabled,
        reversed: true,
        animation: false,
    },
    tooltip: {
        shared: false,
        animation: false,
        enabled: true,
        ...(tooltipFormatter && { useHTML: true, formatter: tooltipFormatter }),
    },
    series,
    plotOptions: {
        series: {
            animation: false,
            shadow: false,
        },
    },
    colors: lineColors,
    credits: { enabled: false },
    exporting: { enabled: false },
});

export const getScatterChartDataSettings = ({
    rowData,
    xMetric,
    yMetric,
    nameField,
    allMetrics = [],
    fieldsMetadata = {},
}) => {
    try {
        const scatterData = rowData.map((row, index) => {
            const metricsData = allMetrics.map(metric => {
                const metricMeta = fieldsMetadata[metric.name] || {};
                return {
                    label: metric.display_name,
                    value: row[metric.name],
                    displayFormat: metricMeta.display_format || metric.displayFormat || { type: 'number' },
                };
            });

            return {
                x: row[xMetric.name],
                y: row[yMetric.name],
                name: row[nameField],
                color: lineColors[index % lineColors.length],
                metricsData,
            };
        });

        if (!scatterData.length) return null;

        const series = [
            {
                name: `${xMetric.display_name} vs ${yMetric.display_name}`,
                data: scatterData,
            },
        ];

        const yAxisList = [{ title: { text: yMetric.display_name } }];

        return { series, yAxisList, xAxisTitle: xMetric.display_name };
    } catch (err) {
        return null;
    }
};

export const buildScatterChartBaseOptions = ({ series, xAxisTitle, yAxisList, tooltipFormatter }) => {
    const baseConfig = buildBaseChartOptions({
        chartType: SCATTER_CHART,
        series,
        legendEnabled: series.length > 1,
        tooltipFormatter,
        xAxisTitle,
    });
    return {
        ...baseConfig,
        yAxis: yAxisList,
        plotOptions: {
            ...baseConfig.plotOptions,
            scatter: {
                marker: {
                    radius: 5,
                    states: {
                        hover: {
                            enabled: true,
                            lineColor: 'rgb(100,100,100)',
                        },
                    },
                },
                states: {
                    hover: {
                        marker: {
                            enabled: false,
                        },
                    },
                },
            },
        },
    };
};

export const buildScatterChartOptions = ({ data, xMetric, yMetric, allMetrics, fieldsMetadata, tooltipFormatter }) => {
    const dataSettings = getScatterChartDataSettings({
        rowData: data,
        xMetric: { name: xMetric.value, display_name: xMetric.label },
        yMetric: { name: yMetric.value, display_name: yMetric.label },
        nameField: 'name',
        allMetrics: allMetrics.map(m => ({ name: m.value, display_name: m.label })),
        fieldsMetadata,
    });

    if (!dataSettings) return null;

    const { series, yAxisList, xAxisTitle } = dataSettings;

    return buildScatterChartBaseOptions({
        series,
        xAxisTitle,
        yAxisList,
        tooltipFormatter,
    });
};

const hasSeriesData = series => {
    if (!series.length) return false;

    return series.some(s =>
        s.data?.some(value => {
            if (value == null) return false;
            if (typeof value === 'object') return value.y != null;
            return true;
        })
    );
};

const buildColumn = (fieldsMetadata, metricName, metricDisplayName, displayFormat) => {
    const metricMetaData = fieldsMetadata?.[metricName] || {};
    return {
        name: metricName,
        display_name: metricDisplayName,
        displayFormat: metricMetaData.display_format || displayFormat || { type: 'number' },
    };
};

export const buildCategoryChartConfig = ({
    chartType,
    categories,
    series,
    yAxisList,
    config,
    isBreakdownChart,
    selectedBreakdown,
    tooltipFormatter,
    isMultiColored = false,
}) => {
    const coloredSeries = isMultiColored
        ? series.map(s => ({
              ...s,
              colorByPoint: true,
          }))
        : series;
    const baseConfig = buildBaseChartOptions({
        chartType,
        series: coloredSeries,
        legendEnabled: yAxisList.length > 1 || series.length > 1,
        xAxisTitle: isBreakdownChart ? selectedBreakdown?.label : undefined,
        xLabelRotation: config.xAxisLabelRotation,
        tooltipFormatter,
    });
    return {
        ...baseConfig,
        xAxis: { ...baseConfig.xAxis, categories, reversed: !isBreakdownChart },
        yAxis: yAxisList,
        plotOptions: {
            ...baseConfig.plotOptions,
            line: {
                marker: { enabled: true, radius: 3 },
                lineWidth: 2,
            },
        },
    };
};

export const buildCategoryChartOptions = ({
    chartType,
    data,
    firstMetric,
    selectedBreakdown,
    fieldsMetadata,
    config,
    isBreakdownChart = !!selectedBreakdown,
    tooltipFormatter,
    isMultiColored = false,
}) => {
    const metricColumn = buildColumn(fieldsMetadata, firstMetric.value, firstMetric.label, firstMetric.display_format);
    const dimension = isBreakdownChart
        ? buildColumn(fieldsMetadata, selectedBreakdown?.value, selectedBreakdown?.label)
        : buildColumn(fieldsMetadata, BREAKDOWN_DIMENSIONS.DATE.value, BREAKDOWN_DIMENSIONS.DATE.label);

    const [categories, series, yAxisList] = getChartDataSettings(
        { name: isBreakdownChart ? COLUMN_CHART : LINE_CHART },
        [metricColumn],
        [dimension],
        [],
        data
    );

    if (!hasSeriesData(series)) return null;

    return buildCategoryChartConfig({
        chartType,
        categories,
        series,
        yAxisList,
        config,
        isBreakdownChart,
        selectedBreakdown,
        tooltipFormatter,
        isMultiColored,
    });
};
